import { forwardRef, memo, useCallback, useState } from 'react';

import { DropDownOptions } from 'devextreme-react/cjs/select-box';
import Form, { Item, RangeRule, RequiredRule } from 'devextreme-react/form';
import { NumberBox } from 'devextreme-react/number-box';
import 'devextreme-react/text-area';
import ValidationGroup from 'devextreme-react/validation-group';

import moment from 'moment';

import { AppCard, AppCol, AppFieldBox, AppGroup, AppRow } from 'components/appComponents';
import { MONEY_FORMAT } from 'constants/formats';
import { useAuth } from 'context/auth';
import { Button, CheckBox, LoadIndicator, SelectBox, Validator } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import { PUMP_ITEMS, PUMP_ITEMS_INTEGER } from 'pages/RequestTask/Task.constants';
import { RequestItemPath, RequestItemPathValue } from 'pages/RequestTask/Task.entity';

import { MethodOfReceptionType } from 'bus/methodOfReception/types';
import { useGetProfileInfoQuery } from 'bus/profile/services/services';
import { PricesItem, RequestItem, RequestPriceResponse } from 'bus/request/types';

import {
  Barter,
  Comment,
  ContractorLegalSelect,
  Customer,
  DateTask,
  Delivery,
  Density,
  Design,
  Employee,
  Factory,
  HeaderLayout,
  MethodReception,
  Nomenclature,
  NomenclatureCustomName,
  Objects,
  PaymentMethod,
  Phone,
  Provider,
  PumpPrice,
  PumpPriceMinimal,
  Quantity,
  Status,
  TimeTask, //Fault,
  Transport,
} from './components';
import { ContractorChangeType } from './components/Customer';

type Props = {
  nomenclatureDataSource: DataSource;
  storeContragent: DataSource;
  storeCustomer: DataSource;
  storeContractorLegal: DataSource;
  methodTypes: MethodOfReceptionType[] | undefined;
  isLoading: boolean;
  form: RequestItem;
  onChangeForm: <T extends RequestItemPath>(name: T, newValue: RequestItemPathValue<T>) => void;
  pricesDataList: PricesItem[] | undefined;
  onGetNeedPrice: (form: RequestItem, init: boolean) => void;
  onSubmit: (e: any) => void;
  onChangeFormGroup: (e: RequestItem) => void;
  isLoadingSubmit: boolean;
  clearButton: (name: keyof RequestPriceResponse) => void;
  clearButtonPrice: () => void;
  clearButtonDelivery: () => void;
  pumpPrice: RequestPriceResponse | undefined;
};

export const TaskDetail = forwardRef<ValidationGroup, Props>(
  (
    {
      nomenclatureDataSource,
      storeContragent,
      storeCustomer,
      storeContractorLegal,
      methodTypes,
      isLoading,
      form,
      pricesDataList,
      isLoadingSubmit,
      pumpPrice,
      onChangeForm,
      onChangeFormGroup,
      onGetNeedPrice,
      onSubmit,
      clearButton,
      clearButtonPrice,
      clearButtonDelivery,
    },
    ref,
  ) => {
    const { getClaims }: any = useAuth();

    const isCanSelectFactoryRequest: boolean = getClaims(['CanSelectFactoryRequest']);

    const { data: profileInfoQuery } = useGetProfileInfoQuery();

    const [taskIsArch] = useState<boolean>(window.location.pathname.indexOf('arch') >= 0);
    const isCanEditArch = getClaims(['CanEditArchiveTask']);
    const readOnly = taskIsArch && !isCanEditArch;

    const handleChangeDate = useCallback(
      (e: string | Date) => {
        onGetNeedPrice({ ...form, date: e }, false);
        onChangeForm('date', e);
      },
      [form, onChangeForm, onGetNeedPrice],
    );

    const handleChangeCustomer = useCallback(
      (value: ContractorChangeType) => {
        onGetNeedPrice({ ...form, ...value }, false);
        onChangeFormGroup({ ...form, ...value });
      },
      [form, onChangeFormGroup, onGetNeedPrice],
    );

    const handleChangeNomenclature = useCallback(
      (value) => {
        onGetNeedPrice({ ...form, ...value }, false);
        onChangeFormGroup({ ...form, ...value });
      },
      [form, onChangeFormGroup, onGetNeedPrice],
    );
    const handleChangeFactory = useCallback(
      (factoryId) => {
        onGetNeedPrice({ ...form, factoryId }, false);
        onChangeForm('factoryId', factoryId);
      },
      [form, onChangeForm, onGetNeedPrice],
    );

    const handleChangeTime = useCallback(
      (e) => {
        onChangeForm('date', moment(e).format('YYYY-MM-DDTHH:mm'));
      },
      [onChangeForm],
    );

    const handleChangeProvider = useCallback(
      (value) => {
        onChangeForm('providerId', value);
      },
      [onChangeForm],
    );

    const handleChangeBuilder = useCallback(
      (value: number | null) => {
        onChangeForm('builderId', value);
      },
      [onChangeForm],
    );

    const handleChangeConsignee = useCallback(
      (value: number | null) => {
        onChangeForm('consigneeId', value);
      },
      [onChangeForm],
    );

    const handleChangeObject = useCallback(
      (value) => {
        onChangeFormGroup({ ...form, ...value });
        onGetNeedPrice({ ...form, ...value }, false);
      },
      [form, onChangeFormGroup, onGetNeedPrice],
    );

    const handleChangeDensity = useCallback(
      (value) => {
        onChangeForm('density', value);
        onGetNeedPrice({ ...form, density: value }, false);
      },
      [form, onChangeForm, onGetNeedPrice],
    );

    const handleChangeNomenclatureCustomName = useCallback(
      (value) => {
        onChangeFormGroup({ ...form, ...value });
      },
      [form, onChangeFormGroup],
    );

    const handleChangeQuantity = useCallback(
      (quantity) => {
        onChangeForm('quantity', quantity);
        onGetNeedPrice({ ...form, quantity }, false);
      },
      [form, onChangeForm, onGetNeedPrice],
    );

    const handleChangePaymentMethod = useCallback(
      (paymentMethod, price) => {
        const currentPaymentMethod = pricesDataList?.find((item) => item.paymentMethod === paymentMethod);

        const currentDeliveryPrice =
          currentPaymentMethod && currentPaymentMethod.deliveryMaxQuantity > form.quantity
            ? currentPaymentMethod.deliveryPrice
            : form.delivery;

        onChangeFormGroup({ ...form, paymentMethod, price, delivery: currentDeliveryPrice });
      },
      [form, onChangeFormGroup, pricesDataList],
    );

    const handleChangeMethodReception = useCallback(
      (value) => {
        onChangeFormGroup({
          ...form,
          methodOfReception: {
            ...form.methodOfReception,
            type: value,
            pumpPhone: '',
            pumpPrice: 0,
            pumpDelivery: 0,
            ...(value !== 3 && { driverId: null }),
          },
          ...(value !== 3 && { glassConcretePrice: 0 }),
        });
      },
      [form, onChangeFormGroup],
    );

    const handleChangeTransport = useCallback(
      (transportId) => {
        onChangeForm('methodOfReception.transportId', transportId);
      },
      [onChangeForm],
    );

    const handleChangeEmployee = useCallback(
      (driversIds: Array<number>, selectedEmployees?: any[]) => {
        if (!driversIds.length) {
          onChangeForm('methodOfReception.driverIds', []);
          onChangeForm('methodOfReception.pumpPhone', '');
          return;
        }
        if (driversIds.length) {
          onChangeForm('methodOfReception.driverIds', driversIds);

          // Автоматически подставляем номер телефона первого выбранного водителя
          if (selectedEmployees && selectedEmployees.length > 0) {
            const firstDriver = selectedEmployees[0];
            if (firstDriver && firstDriver.phone) {
              onChangeForm('methodOfReception.pumpPhone', firstDriver.phone);
            }
          }
        }
      },
      [onChangeForm],
    );

    const currentTotalSum = (form.price * form.quantity + form.delivery + form.methodOfReceptionTotalSum).toFixed(2);

    const validMinRange = form.paymentMethod === 2 ? 0 : 1;

    if (isLoading || !profileInfoQuery) {
      return <div>Загрузка...</div>;
    }

    return (
      <div className="container-dev">
        <form onSubmit={onSubmit} className="mt-2" action="form-task-detail">
          <ValidationGroup ref={ref}>
            <HeaderLayout
              taskIsArch={taskIsArch}
              onSubmit={onSubmit}
              isCanEditArch={isCanEditArch}
              isLoading={isLoadingSubmit}
            />
            {isLoading ? (
              <LoadIndicator width={40} height={40} />
            ) : (
              <div>
                <Form>
                  <Item colSpan={12} colCount={12} itemType="group">
                    <AppRow>
                      <AppCol col={8}>
                        <AppCard className="pa-2">
                          <AppGroup title="Основная информация">
                            <AppRow className="mt-2">
                              <AppCol col={6}>
                                <DateTask
                                  value={form.date}
                                  onChange={handleChangeDate}
                                  readOnly={readOnly}
                                  taskIsArch={taskIsArch}
                                />
                              </AppCol>
                              <AppCol col={6}>
                                <TimeTask value={form.date} onChange={handleChangeTime} readOnly={readOnly} />
                              </AppCol>
                            </AppRow>

                            <AppRow>
                              <AppCol col={6}>
                                <Provider
                                  value={form.providerId}
                                  readOnly={readOnly}
                                  onChange={handleChangeProvider}
                                  storeContragent={storeContragent}
                                />
                              </AppCol>
                              <AppCol col={6}>
                                <ContractorLegalSelect
                                  label="Подрядчик"
                                  placeholder="Выберите подрядчика"
                                  value={form.builderId}
                                  readOnly={readOnly}
                                  storeContractorLegal={storeContractorLegal}
                                  onChange={handleChangeBuilder}
                                />
                              </AppCol>
                              <AppCol col={6}>
                                <Customer
                                  value={form.contractorId}
                                  readOnly={readOnly}
                                  onChange={handleChangeCustomer}
                                  storeCustomer={storeCustomer}
                                />
                              </AppCol>
                              <AppCol col={6}>
                                <ContractorLegalSelect
                                  label="Грузополучатель"
                                  placeholder="Выберите грузополучателя"
                                  value={form.consigneeId}
                                  readOnly={readOnly}
                                  storeContractorLegal={storeContractorLegal}
                                  onChange={handleChangeConsignee}
                                />
                              </AppCol>
                              <AppCol col={12}>
                                <Objects
                                  contractorId={form.contractorId}
                                  methodOfReceptionType={Number(form.methodOfReception.type)}
                                  readOnly={readOnly}
                                  value={form.objectId}
                                  contractorType={form.contractorType}
                                  onChange={handleChangeObject}
                                />
                              </AppCol>

                              <AppCol col={3}>
                                <Nomenclature
                                  value={form.nomenclatureId}
                                  onChange={handleChangeNomenclature}
                                  readOnly={readOnly}
                                  nomenclatureDataSource={nomenclatureDataSource}
                                />
                              </AppCol>
                              <AppCol col={3}>
                                <Density value={form.density} onChange={handleChangeDensity} readOnly={readOnly} />
                              </AppCol>
                              <AppCol col={6}>
                                <NomenclatureCustomName
                                  value={form.customNomenclatureNameId}
                                  nomenclatureId={form.nomenclatureId}
                                  onChange={handleChangeNomenclatureCustomName}
                                  readOnly={readOnly}
                                />
                              </AppCol>
                            </AppRow>

                            <AppRow>
                              <AppCol col={3}>
                                <Quantity value={form.quantity} onChange={handleChangeQuantity} readOnly={readOnly} />
                              </AppCol>
                              <AppCol col={3}>
                                <PaymentMethod
                                  onChange={handleChangePaymentMethod}
                                  value={form.paymentMethod}
                                  pricesDataList={pricesDataList}
                                />
                              </AppCol>
                              <AppCol col={isCanSelectFactoryRequest ? 3 : 6}>
                                <Phone
                                  label="Телефон на объекте"
                                  value={form.phone}
                                  onChange={(phone) => {
                                    onChangeForm('phone', phone);
                                  }}
                                  readOnly={readOnly}
                                />
                              </AppCol>
                              {isCanSelectFactoryRequest && (
                                <AppCol col={3}>
                                  <Factory value={form.factoryId} onChange={handleChangeFactory} readOnly={readOnly} />
                                </AppCol>
                              )}
                              <div className="mb-3"></div>
                            </AppRow>
                          </AppGroup>

                          <AppGroup title="Метод приёма">
                            <MethodReception
                              methodTypes={methodTypes}
                              value={form.methodOfReception.type ?? undefined}
                              onChange={handleChangeMethodReception}
                            />
                            {form.methodOfReception.type === 3 && (
                              <AppRow className="mt-2">
                                <AppCol col={4}>
                                  <Transport
                                    value={form.methodOfReception.transportId}
                                    onChange={handleChangeTransport}
                                    readOnly={readOnly}
                                  />
                                </AppCol>
                                <AppCol col={4}>
                                  <Employee
                                    value={form.methodOfReception.driverIds}
                                    disabled={form.methodOfReception.driverId! < 0}
                                    onChange={handleChangeEmployee}
                                    readOnly={readOnly}
                                  />
                                </AppCol>

                                <AppCol col={4}>
                                  <Phone
                                    label="Телефон водителя"
                                    value={form.methodOfReception.pumpPhone}
                                    onChange={(pumpPhone) => {
                                      onChangeForm('methodOfReception.pumpPhone', pumpPhone);
                                    }}
                                    readOnly={readOnly}
                                  />
                                </AppCol>
                              </AppRow>
                            )}
                            <AppRow>
                              <AppCol col={12}>
                                <Comment
                                  readOnly={readOnly}
                                  value={form.comment}
                                  onChange={(comment) => {
                                    onChangeForm('comment', comment);
                                  }}
                                />
                              </AppCol>
                            </AppRow>
                            <AppRow>
                              <AppCol col={12}>
                                <Design
                                  readOnly={readOnly}
                                  value={form.design}
                                  onChange={(design) => {
                                    onChangeForm('design', design);
                                  }}
                                />
                              </AppCol>
                            </AppRow>
                          </AppGroup>
                        </AppCard>
                      </AppCol>

                      <AppCol col={4}>
                        <Status
                          readOnly={readOnly}
                          value={form.requestStatus}
                          onChange={(request) => {
                            onChangeFormGroup(request);
                          }}
                        />

                        <AppCard className="pa-2">
                          <AppGroup title="Оплата бетона">
                            <AppRow className="mt-2 mb-2">
                              <AppCol col={12}>
                                <AppFieldBox label="Цена за куб*" labelTop>
                                  <AppRow>
                                    <AppCol col={11}>
                                      <NumberBox
                                        stylingMode="underlined"
                                        readOnly={readOnly}
                                        placeholder="Цена за куб"
                                        format="###0.##"
                                        value={form.price}
                                        onValueChanged={(e) => {
                                          onChangeForm('price', e.value);
                                          onChangeForm('priceFilledManually', true);
                                        }}
                                      >
                                        <Validator>
                                          <RequiredRule message="Заполните поле Цена за куб" />
                                          <RangeRule min={validMinRange} message="Поле должно быть больше 0" />
                                        </Validator>
                                      </NumberBox>
                                    </AppCol>
                                    <AppCol col={1}>
                                      <Button icon="revert" onClick={clearButtonPrice} />
                                    </AppCol>
                                  </AppRow>
                                </AppFieldBox>

                                <Barter
                                  onChangeText={(barterValue) => {
                                    onChangeForm('barterValue', barterValue);
                                  }}
                                  paymentMethod={form.paymentMethod}
                                  valueText={form.barterValue}
                                  readOnly={readOnly}
                                />
                              </AppCol>
                              <AppCol col={12}>
                                <Delivery
                                  value={form.delivery}
                                  readOnly={readOnly}
                                  onChange={(delivery) => {
                                    onChangeForm('delivery', delivery);
                                    onChangeForm('deliveryFilledManually', true);
                                  }}
                                  delivery={form.delivery}
                                  price={form.price}
                                  pumpDelivery={form.methodOfReception.pumpDelivery || 0}
                                  quantity={form.quantity}
                                  onRevert={clearButtonDelivery}
                                />
                              </AppCol>
                            </AppRow>
                            <div className="mt-4 mb-4" style={{ borderTop: '1px dashed #CCC' }}></div>
                          </AppGroup>

                          {form.methodOfReception.type === 1 ? (
                            <AppGroup title="Доплата за рюмку">
                              <AppRow className="mt-2 mb-2">
                                <AppCol>
                                  <NumberBox
                                    stylingMode="underlined"
                                    placeholder="0"
                                    mode="text"
                                    value={form.methodOfReception.glassConcretePrice || 0}
                                    readOnly={readOnly}
                                    min={0}
                                    onValueChange={(e) => {
                                      onChangeForm('methodOfReception.glassConcretePrice', e || 0);
                                    }}
                                  >
                                    <Validator>
                                      <RequiredRule message="Заполните поле доплату за рюмку" />
                                      <RangeRule min={0} message="Поле должно быть больше 0" />
                                    </Validator>
                                  </NumberBox>
                                </AppCol>
                              </AppRow>
                              <div className="mt-4 mb-4" style={{ borderTop: '1px dashed #CCC' }}></div>
                            </AppGroup>
                          ) : null}

                          <AppGroup title={form.methodOfReception.type === 3 ? 'Оплата насоса' : ''}>
                            <AppRow>
                              <AppCol col={12}>
                                {form.methodOfReception.type === 3 && (
                                  <div className="pt-1">
                                    <AppFieldBox label="Кол-во кубов" labelTop>
                                      <AppRow>
                                        <AppCol col={11}>
                                          <NumberBox
                                            stylingMode="underlined"
                                            placeholder="0"
                                            mode="text"
                                            value={form.methodOfReception.pumpQuantity || 0}
                                            readOnly={readOnly}
                                            min={0}
                                            onValueChange={(e) => {
                                              onChangeForm('methodOfReception.pumpQuantity', e || 0);
                                              onChangeForm('methodOfReception.isFilledManually', true);
                                            }}
                                          >
                                            <Validator>
                                              <RequiredRule message="Заполните поле Кол-во кубов" />
                                              <RangeRule min={0} message="Поле должно быть больше 0" />
                                            </Validator>
                                          </NumberBox>
                                        </AppCol>
                                        <AppCol col={1}>
                                          <Button
                                            icon="revert"
                                            onClick={() => {
                                              onChangeForm('methodOfReception.isFilledManually', false);
                                            }}
                                          />
                                        </AppCol>
                                      </AppRow>
                                    </AppFieldBox>

                                    <PumpPrice
                                      readOnly={readOnly}
                                      value={form.methodOfReception.pumpPrice}
                                      onChange={(pumpPrice) => {
                                        onChangeForm('methodOfReception.pumpPrice', pumpPrice);
                                        onChangeForm('methodOfReception.pumpPriceFilledManually', true);
                                      }}
                                      clearButton={() => {
                                        clearButton('price');
                                        onChangeForm('methodOfReception.pumpPriceFilledManually', false);
                                      }}
                                    />

                                    <PumpPriceMinimal
                                      value={form.methodOfReception.pumpPriceMinimal}
                                      onChange={(pumpPriceMinimal) => {
                                        onChangeForm('methodOfReception.pumpPriceMinimal', pumpPriceMinimal);
                                        onChangeForm('methodOfReception.pumpPriceMinimalFilledManually', true);
                                      }}
                                      readOnly={readOnly}
                                      clearButton={() => {
                                        clearButton('pumpPriceMinimal');
                                        onChangeForm('methodOfReception.pumpPriceMinimalFilledManually', false);
                                      }}
                                    />

                                    <AppFieldBox label="Подача" labelTop>
                                      <AppRow>
                                        <AppCol col={11}>
                                          <NumberBox
                                            stylingMode="underlined"
                                            placeholder="0"
                                            mode="text"
                                            value={form.methodOfReception.pumpInnings}
                                            readOnly={readOnly}
                                            min={0}
                                            onValueChange={(e) => {
                                              onChangeForm('methodOfReception.pumpInnings', e);
                                              onChangeForm('methodOfReception.pumpInningsFilledManually', true);
                                            }}
                                          />
                                        </AppCol>
                                        <AppCol col={1}>
                                          <Button
                                            icon="revert"
                                            onClick={() => {
                                              clearButton('pumpInnings');
                                              onChangeForm('methodOfReception.pumpInningsFilledManually', false);
                                            }}
                                          />
                                        </AppCol>
                                      </AppRow>
                                    </AppFieldBox>

                                    <AppFieldBox label="Гаситель" labelTop>
                                      <AppRow>
                                        <AppCol col={10}>
                                          <NumberBox
                                            stylingMode="underlined"
                                            placeholder="0"
                                            mode="text"
                                            value={form.methodOfReception.pumpQuencher}
                                            readOnly={readOnly}
                                            min={0}
                                            onValueChange={(e) => {
                                              onChangeForm('methodOfReception.pumpQuencher', e);
                                              onChangeForm('methodOfReception.pumpQuencherFilledManually', true);
                                            }}
                                          />
                                        </AppCol>
                                        <AppCol col={1}>
                                          <CheckBox
                                            value={form.methodOfReception.useQuencher}
                                            onValueChange={(e) => {
                                              onChangeForm('methodOfReception.useQuencher', Boolean(e));

                                              if (!e) {
                                                onChangeForm('methodOfReception.pumpQuencher', 0);
                                                onChangeForm('methodOfReception.pumpQuencherFilledManually', false);
                                              } else {
                                                onChangeForm(
                                                  'methodOfReception.pumpQuencher',
                                                  pumpPrice?.pumpQuencher || 0,
                                                );
                                              }
                                            }}
                                            className="pt-1"
                                          />
                                        </AppCol>
                                        <AppCol col={1}>
                                          <Button
                                            icon="revert"
                                            onClick={() => {
                                              clearButton('pumpQuencher');
                                              onChangeForm('methodOfReception.pumpQuencherFilledManually', false);
                                            }}
                                          />
                                        </AppCol>
                                      </AppRow>
                                    </AppFieldBox>

                                    <AppFieldBox label="Переезд" labelTop>
                                      <AppRow>
                                        <AppCol col={8}>
                                          <AppRow>
                                            <AppCol col={9}>
                                              <NumberBox
                                                stylingMode="underlined"
                                                placeholder="0"
                                                mode="text"
                                                value={form.methodOfReception.pumpMoving}
                                                readOnly={readOnly}
                                                min={0}
                                                onValueChange={(e) => {
                                                  onChangeForm('methodOfReception.pumpMoving', e);
                                                  onChangeForm('methodOfReception.pumpMovingFilledManually', true);
                                                }}
                                                format={MONEY_FORMAT}
                                              />
                                            </AppCol>
                                            <AppCol col={3}>
                                              <Button
                                                icon="revert"
                                                onClick={() => {
                                                  clearButton('pumpMoving');
                                                  onChangeForm('methodOfReception.pumpMovingFilledManually', false);
                                                }}
                                              />
                                            </AppCol>
                                          </AppRow>
                                        </AppCol>

                                        <AppCol col={4}>
                                          <SelectBox
                                            stylingMode="underlined"
                                            placeholder=""
                                            items={PUMP_ITEMS_INTEGER}
                                            value={form.methodOfReception.pumpMovingCount ?? 0}
                                            onItemClick={(e) => {
                                              onChangeForm('methodOfReception.pumpMovingCount', e.itemData);

                                              if (e.itemData === 0) {
                                                onChangeForm('methodOfReception.pumpMoving', 0);
                                                onChangeForm('methodOfReception.pumpMovingFilledManually', false);
                                              } else {
                                                onChangeForm(
                                                  'methodOfReception.pumpMoving',
                                                  pumpPrice?.pumpMoving || 0,
                                                );
                                              }
                                            }}
                                          >
                                            <DropDownOptions closeOnOutsideClick={true} showTitle={false} />
                                          </SelectBox>
                                        </AppCol>
                                      </AppRow>
                                    </AppFieldBox>

                                    <AppFieldBox label="Трубы" labelTop>
                                      <AppRow>
                                        <AppCol col={8}>
                                          <AppRow>
                                            <AppCol col={9}>
                                              <NumberBox
                                                stylingMode="underlined"
                                                placeholder="0"
                                                mode="text"
                                                value={form.methodOfReception.pumpPipes}
                                                readOnly={readOnly}
                                                min={0}
                                                format="fixedPoint"
                                                onValueChange={(e) => {
                                                  onChangeForm('methodOfReception.pumpPipes', e);
                                                  onChangeForm('methodOfReception.pumpPipesFilledManually', true);
                                                }}
                                              />
                                            </AppCol>
                                            <AppCol col={3}>
                                              <Button
                                                icon="revert"
                                                onClick={() => {
                                                  clearButton('pumpPipes');
                                                  onChangeForm('methodOfReception.pumpPipesFilledManually', false);
                                                }}
                                              />
                                            </AppCol>
                                          </AppRow>
                                        </AppCol>
                                        <AppCol col={4}>
                                          <SelectBox
                                            stylingMode="underlined"
                                            placeholder=""
                                            items={PUMP_ITEMS_INTEGER}
                                            value={form.methodOfReception.pumpPipesCount ?? 0}
                                            onItemClick={(e) => {
                                              onChangeForm('methodOfReception.pumpPipesCount', e.itemData);

                                              if (e.itemData === 0) {
                                                onChangeForm('methodOfReception.pumpPipes', 0);
                                                onChangeForm('methodOfReception.pumpPipesFilledManually', false);
                                              } else {
                                                onChangeForm('methodOfReception.pumpPipes', pumpPrice?.pumpPipes || 0);
                                              }
                                            }}
                                          >
                                            <DropDownOptions closeOnOutsideClick={true} showTitle={false} />
                                          </SelectBox>
                                        </AppCol>
                                      </AppRow>
                                    </AppFieldBox>

                                    <AppFieldBox label="Простой" labelTop>
                                      <AppRow>
                                        <AppCol col={8}>
                                          <AppRow>
                                            <AppCol col={9}>
                                              <NumberBox
                                                stylingMode="underlined"
                                                placeholder="0"
                                                format={MONEY_FORMAT}
                                                mode="text"
                                                value={form.methodOfReception.downtime}
                                                readOnly={readOnly}
                                                min={0}
                                                onValueChange={(e) => {
                                                  onChangeForm('methodOfReception.downtime', e);
                                                  onChangeForm('methodOfReception.downtimeFilledManually', true);
                                                }}
                                              />
                                            </AppCol>
                                            <AppCol col={3}>
                                              <Button
                                                icon="revert"
                                                onClick={() => {
                                                  clearButton('downtime');
                                                  onChangeForm('methodOfReception.downtimeFilledManually', false);
                                                }}
                                              />
                                            </AppCol>
                                          </AppRow>
                                        </AppCol>
                                        <AppCol col={4}>
                                          <SelectBox
                                            stylingMode="underlined"
                                            placeholder=""
                                            items={PUMP_ITEMS}
                                            value={form.methodOfReception.downtimeHours}
                                            onItemClick={(e) => {
                                              onChangeForm('methodOfReception.downtimeHours', e.itemData);

                                              if (e.itemData === 0) {
                                                onChangeForm('methodOfReception.downtime', 0);
                                                onChangeForm('methodOfReception.downtimeFilledManually', false);
                                              } else {
                                                onChangeForm('methodOfReception.downtime', pumpPrice?.downtime || 0);
                                              }
                                            }}
                                          >
                                            <DropDownOptions closeOnOutsideClick={true} showTitle={false} />
                                          </SelectBox>
                                        </AppCol>
                                      </AppRow>
                                    </AppFieldBox>
                                    <AppRow>
                                      <AppCol col={12}>
                                        <AppFieldBox label="Кол-во километров" labelTop>
                                          <NumberBox
                                            stylingMode="underlined"
                                            placeholder="0"
                                            format={MONEY_FORMAT}
                                            mode="text"
                                            value={form.methodOfReception.distance}
                                            readOnly={readOnly}
                                            min={0}
                                            onValueChange={(e) => {
                                              onChangeForm('methodOfReception.distance', e);
                                            }}
                                          />
                                        </AppFieldBox>
                                      </AppCol>
                                    </AppRow>

                                    <div className="mt-2 mb-2" style={{ fontWeight: 'bold', fontSize: 16 }}>
                                      Итого: {form.methodOfReception.pumpTotal}
                                    </div>
                                  </div>
                                )}

                                <div
                                  style={{
                                    fontWeight: 'bold',
                                    fontSize: 16,
                                    paddingTop: 5,
                                  }}
                                >
                                  С учетом погрешности: {form.requestTotalWithFault.toFixed(2)}
                                </div>

                                <div
                                  style={{
                                    fontWeight: 'bold',
                                    fontSize: 20,
                                    borderTop: form.methodOfReception.type === 3 ? '1px dashed' : 'none',
                                    paddingTop: 5,
                                  }}
                                >
                                  Общая сумма: {currentTotalSum}
                                </div>
                              </AppCol>
                            </AppRow>
                          </AppGroup>
                        </AppCard>
                      </AppCol>
                    </AppRow>
                  </Item>
                </Form>
              </div>
            )}
          </ValidationGroup>
        </form>
      </div>
    );
  },
);

export default memo(TaskDetail);
